// Trip utility functions

import { Trip } from '../api/tripsApi';

export const normalizeStatus = (status: string): string => {
  return status.toLowerCase().replace(/\s+/g, '_');
};

export const getStatusDisplay = (status: string): string => {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case 'draft':
      return '🚀 Draft';
    case 'in_progress':
      return '🚀 In Progress';
    case 'confirmed':
      return '✅ Confirmed';
    case 'planning':
      return '📋 Planning';
    case 'completed':
      return '✅ Completed';
    default:
      return status;
  }
};

export const formatBudget = (budget: number | string | { amount: number; currency: string } | undefined): string | null => {
  if (budget === undefined || budget === null || budget === '') {
    return null;
  }
  
  // Handle object format { amount: number, currency: string }
  if (typeof budget === 'object' && budget !== null) {
    const currencySymbols: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'INR': '₹',
      'AUD': 'A$',
      'CAD': 'C$',
      'CHF': 'Fr',
      'SEK': 'kr',
      'NZD': 'NZ$'
    };
    const symbol = currencySymbols[budget.currency] || budget.currency;
    return `${symbol}${budget.amount.toLocaleString()}`;
  }
  
  // Handle string or number format (legacy)
  const numBudget = typeof budget === 'string' ? parseFloat(budget) : budget;
  if (isNaN(numBudget)) {
    return String(budget); // Return as is if not a valid number
  }
  return `$${numBudget.toLocaleString()}`;
};

export const formatDestinations = (destinations: string[] | undefined): string => {
  return destinations && destinations.length > 0 ? destinations.join(', ') : 'No destinations';
};

/** True when the record is a top-level trip, not an activity row from the trips index. */
export const isTripRecord = (trip: Trip): boolean => {
  const id = String(trip.id ?? '');
  if (!id || id.includes('#ACTIVITY#')) return false;
  if ((trip as Trip & { tripId?: string }).tripId) return false;
  return true;
};

/** Trips that have enough itinerary metadata to show in profile/trip lists. */
export const isSubstantiveTrip = (trip: Trip): boolean => {
  if (!isTripRecord(trip)) return false;
  return (
    (trip.destinations?.length ?? 0) > 0 ||
    Boolean(trip.dates?.startDate) ||
    Boolean(trip.conversation_id)
  );
};

export const filterTripRecords = (trips: Trip[]): Trip[] => trips.filter(isSubstantiveTrip);

export const getTripImage = (images: string[] | undefined): string => {
  return images && images.length > 0 ? images[0] : '✈️';
};

export const getFilteredTrips = (trips: Trip[]) => {
  const drafted = trips.filter(trip => normalizeStatus(trip.status) === 'draft');
  const inProgress = trips.filter(trip => normalizeStatus(trip.status) === 'in_progress');
  const upcoming = trips.filter(trip => {
    const normalized = normalizeStatus(trip.status);
    return normalized === 'confirmed' || normalized === 'planning';
  });
  const past = trips.filter(trip => normalizeStatus(trip.status) === 'completed');

  return { drafted, inProgress, upcoming, past };
};

